import React, {forwardRef, useRef, useMemo} from 'react';
import {
    FloatingFocusManager,
    FloatingList,
    FloatingNode,
    FloatingPortal,
    useTransitionStatus,
} from '@floating-ui/react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import {useReturnFocusState} from './hooks/use-return-focus-state';
import {zIndexStore} from './utils/z-index-store';

import styles from './floating.component.module.styl';

import type {ComponentProps, ReactNode} from 'react';
import type {FloatingContext} from '@floating-ui/react';


const classNames = withCssModule(styles);

type NativeProps = Omit<ComponentProps<'div'>, 'ref'>;

export interface FloatingPopoverProps extends NativeProps {
    /** Content rendered inside the floating panel. */
    children: ReactNode,
    /**
     * When `true`, keeps the panel mounted in the DOM even when the Floating UI
     * `context.open` is `false` (bypasses the `isMounted` transition check). Used
     * internally by `DropdownBase` for the `shouldUnmount=false` scenario.
     */
    open?: boolean,
    /**
     * Mutable ref array (from `useFloating`) that holds a reference to each
     * focusable list item element. Required by `FloatingList` for keyboard
     * navigation and typeahead support.
     */
    listItemsRef: React.MutableRefObject<(HTMLElement | null)[]>,
    /**
     * Mutable ref array of string labels corresponding to each list item. Used
     * by Floating UI's `useTypeahead` to match key presses to items.
     */
    labelsRef?: React.MutableRefObject<(string | null)[]>,
    /** Returns the current Floating UI `FloatingContext` used by all sub-hooks. */
    getContext: () => FloatingContext,
    /**
     * Props forwarded to `FloatingPortal`. Use `root` or `id` to control where
     * the portal is mounted (defaults to `document.body`).
     */
    portalProps?: ComponentProps<typeof FloatingPortal>,
    /**
     * Props forwarded to `FloatingFocusManager`. Use `initialFocus`, `returnFocus`,
     * `modal`, or `order` to tune focus-trap behavior inside the panel.
     */
    floatingFocusManagerProps?: Partial<ComponentProps<typeof FloatingFocusManager>>,
    /**
     * When `true`, applies the `flip` CSS modifier class that mirrors the panel's
     * border-radius so top-positioned panels look visually correct. Set
     * automatically by `DropdownBase` when the panel is placed above the anchor.
     */
    flip?: boolean,
    /** Floating UI node ID assigned by `useFloatingNodeId`. Used by `FloatingNode`
     *  to register this panel in the floating tree for nested-panel coordination. */
    nodeId: string | undefined,
}

/**
 * Low-level floating panel primitive built on Floating UI. Use together with the
 * `useFloating` hook, which provides `getContext`, `listItemsRef`, and the
 * forwarded `ref`.
 *
 * Handles the complete lifecycle of a positioned overlay:
 * - Mounts inside a `FloatingPortal` (appended to `document.body` by default;
 *   override via `portalProps`).
 * - Wraps content in `FloatingFocusManager` for keyboard trap and focus
 *   restoration (configure via `floatingFocusManagerProps`).
 * - Applies a 150 ms CSS transition driven by `useTransitionStatus`; the
 *   element stays in the DOM during the exit animation.
 * - Manages `z-index` automatically via an internal `zIndexStore` so nested
 *   panels stack correctly.
 *
 * Prefer the higher-level `Dropdown`, `Tooltip`, or `FloatingMenu` components
 * for most use-cases. Use `FloatingPopover` directly only when you need
 * fine-grained control over the Floating UI context.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-floating--documentation}
 */
export const FloatingPopover = forwardRef<HTMLDivElement, FloatingPopoverProps>(({
    children,
    open,
    listItemsRef,
    labelsRef,
    getContext,
    portalProps,
    floatingFocusManagerProps,
    flip,
    nodeId,
    ...nativeProps
}, ref) => {
    const context = getContext();

    // do not update children when closing with transition
    const childrenRef = useRef(children);
    if (context.open) {
        childrenRef.current = children;
    }

    const {isMounted, status} = useTransitionStatus(context, {
        duration: 150,
    });

    const zIndex = useMemo(() => {
        if (context.open) {
            return zIndexStore.next();
        }

        return undefined;
    }, [context.open]);

    const floatingClasses = classNames(nativeProps.className, 'floating', status, {flip});

    const returnFocus = useReturnFocusState(context);

    return (isMounted || open) && childrenRef.current ? (
        <FloatingNode id={nodeId}>
            <FloatingList elementsRef={listItemsRef} labelsRef={labelsRef}>
                <FloatingPortal {...portalProps}>
                    <FloatingFocusManager
                        returnFocus={returnFocus}
                        disabled={status !== 'open'}
                        {...floatingFocusManagerProps}
                        context={context}>
                        <div
                            ref={ref}
                            {...nativeProps}
                            style={{
                                zIndex,
                                ...nativeProps.style,
                            }}
                            className={floatingClasses}>
                            {childrenRef.current}
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            </FloatingList>
        </FloatingNode>
    ) : null;
});

FloatingPopover.displayName = 'FloatingPopover';
